import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useContext,
} from "react";
import { AppContext } from "../../context/AppContext";
import {
  Download,
  Store,
  ChevronDown,
  ChevronRight,
  BarChart3,
  Receipt,
  IndianRupee,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { postRequest, simpleGetCall } from "../../api/ApiServices";
import { filter } from "lodash";
import { notifyError, notifySuccess } from "../../toaster/notify";
import { Spinner } from "react-bootstrap";
import StoreSelector from "../../pages/StoreSelector";

// --- CONFIGURATION ---

const CONFIG = {
  USE_MOCK_DATA: true, // Set to false to use real API

  API_ENDPOINT: "/api/sales/item-wise-report",

  CURRENCY: "INR",

  LOCALE: "en-IN",

  RESTAURANT_NAME: "ZAM ZAM Restaurants YMR",
};

const filterRestaurantName = (resId = null, storesList = []) => {
  if (resId) {
    const item = storesList?.filter((item) => item.value == resId);

    return item?.[0]?.label;
  }
};

// --- MOCK DATA ---

const MOCK_API_RESPONSE = {
  summary: {
    totalQuantity: 22,

    totalAmount: 6595,

    minQuantity: 1,

    minAmount: 45,

    maxQuantity: 6,

    maxAmount: 1945,

    avgQuantity: 1.83,

    avgAmount: 549.58,
  },

  categoryWiseData: [
    {
      categoryId: 4718,

      categoryName: "BREADS",

      items: [
        {
          itemId: 67652,
          itemName: "BUTTER NAAN",
          variationName: null,
          quantity: 3,
          amount: 45,
          sapCode: "",
          shortCode: "",
        },
      ],

      subTotalQuantity: 3,

      subTotalAmount: 45,
    },

    {
      categoryId: 4730,

      categoryName: "HOT TREATS",

      items: [
        {
          itemId: 67812,
          itemName: "BLOCKBUSTER SHAWAYYA FULL",
          variationName: "4 Paratta",
          quantity: 1,
          amount: 675,
          sapCode: "",
          shortCode: "",
        },

        {
          itemId: 67804,
          itemName: "BLOCK BUSTER SHAWAYYA HALF",
          variationName: "2 Khubz",
          quantity: 2,
          amount: 670,
          sapCode: "",
          shortCode: "",
        },

        {
          itemId: 67804,
          itemName: "BLOCK BUSTER SHAWAYYA HALF",
          variationName: "2 Paratta",
          quantity: 6,
          amount: 1945,
          sapCode: "",
          shortCode: "",
        },

        {
          itemId: 67691,
          itemName: "CHICKEN TIKKA 12 PCS",
          variationName: null,
          quantity: 2,
          amount: 1320,
          sapCode: "",
          shortCode: "",
        },

        {
          itemId: 69695,
          itemName: "DYNAMITE AL FAHAM",
          variationName: "HALF+2PAROTA/KHUBZ",
          quantity: 1,
          amount: 450,
          sapCode: "",
          shortCode: "",
        },

        {
          itemId: 67733,
          itemName: "TANDOOR HALF",
          variationName: "2 Paratta",
          quantity: 1,
          amount: 360,
          sapCode: "",
          shortCode: "",
        },
      ],

      subTotalQuantity: 13,

      subTotalAmount: 5420,
    },

    {
      categoryId: 4735,

      categoryName: "MANDI SPECIALS",

      items: [
        {
          itemId: 69694,
          itemName: "DYNAMITE AL FAHAM MANDI",
          variationName: "QTR",
          quantity: 1,
          amount: 300,
          sapCode: "",
          shortCode: "",
        },
      ],

      subTotalQuantity: 1,

      subTotalAmount: 300,
    },

    {
      categoryId: 4741,

      categoryName: "SOUP DISHES",

      items: [
        {
          itemId: 67601,
          itemName: "CHICKEN CLEAR SOUP",
          variationName: null,
          quantity: 1,
          amount: 120,
          sapCode: "",
          shortCode: "",
        },
      ],

      subTotalQuantity: 1,

      subTotalAmount: 120,
    },

    {
      categoryId: 4885,

      categoryName: "STARTERS",

      items: [
        {
          itemId: 67704,
          itemName: "BEEF DRAGON",
          variationName: "HALF",
          quantity: 1,
          amount: 220,
          sapCode: "",
          shortCode: "",
        },

        {
          itemId: 67569,
          itemName: "DRAGON CHICKEN",
          variationName: "HALF",
          quantity: 2,
          amount: 220,
          sapCode: "",
          shortCode: "",
        },
      ],

      subTotalQuantity: 3,

      subTotalAmount: 440,
    },

    {
      categoryId: 4743,

      categoryName: "VEGTABLES DISHES",

      items: [
        {
          itemId: 67486,
          itemName: "MUSHROOM METHI",
          variationName: null,
          quantity: 1,
          amount: 270,
          sapCode: "",
          shortCode: "",
        },
      ],

      subTotalQuantity: 1,

      subTotalAmount: 270,
    },
  ],
};

// --- API SERVICE ---

const fetchReportData = async () => {
  if (CONFIG.USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(MOCK_API_RESPONSE);
      }, 800);
    });
  } else {
    try {
      const response = await fetch(CONFIG.API_ENDPOINT);

      if (!response.ok) throw new Error("Network response was not ok");

      return await response.json();
    } catch (error) {
      console.error("API Error:", error);

      throw error;
    }
  }
};

// --- UTILITY FUNCTIONS ---

const formatCurrency = (val) => {
  return new Intl.NumberFormat(CONFIG.LOCALE, {
    style: "currency",

    currency: CONFIG.CURRENCY,

    minimumFractionDigits: 0,

    maximumFractionDigits: 0,
  }).format(val);
};

// --- MEMOIZED SUB-COMPONENTS ---

const SummaryCard = React.memo(
  ({ title, value, subValue, icon, colorClass, rotateIcon }) => (
    <div className="card h-100 shadow-sm border-0">
      <div className="card-body p-3 d-flex flex-column justify-content-between">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <p
              className="small text-secondary fw-bold text-uppercase mb-1"
              style={{ fontSize: "11px" }}
            >
              {title}
            </p>

            <h3 className="h5 fw-bold text-dark mb-0">{value}</h3>
          </div>

          <div
            className={`p-2 rounded ${colorClass}`}
            style={rotateIcon ? { transform: "rotate(180deg)" } : {}}
          >
            {icon}
          </div>
        </div>

        <p
          className="mb-0 text-secondary fw-medium"
          style={{ fontSize: "10px" }}
        >
          {subValue}
        </p>
      </div>
    </div>
  )
);

const CategoryRow = React.memo(
  ({ category, idx, isExpanded, toggleCategory }) => {
    return (
      <React.Fragment>
        {/* Category Header Row */}

        <tr
          className="bg-light"
          onClick={() => toggleCategory(category.categoryId)}
          style={{ cursor: "pointer" }}
          aria-expanded={isExpanded}
          aria-controls={`cat-content-${category.categoryId}`}
        >
          <td className="px-2 px-md-3 py-2 fw-bold text-primary small d-flex align-items-center gap-2 border-bottom-0">
            {isExpanded ? (
              <ChevronDown size={14} />
            ) : (
              <ChevronRight size={14} />
            )}

            {category.categoryName}
          </td>

          <td className="px-2 px-md-3 py-2 border-bottom-0"></td>

          <td className="d-none d-md-table-cell px-3 py-2 border-bottom-0"></td>

          <td className="px-2 px-md-3 py-2 border-bottom-0"></td>

          <td className="px-2 px-md-3 py-2 border-bottom-0"></td>
        </tr>

        {/* Items Rows */}

        {isExpanded &&
          category.items.map((item, itemIdx) => (
            <tr
              key={`${category.categoryId}-${item.itemId}-${itemIdx}`}
              id={`cat-content-${category.categoryId}`}
            >
              <td className="px-2 px-md-3 py-1"></td>

              <td className="px-2 px-md-3 py-1">
                <div className="fw-medium text-dark small text-break">
                  {item.itemName}
                </div>

                {/* {item.variationName && (

              <span className="badge bg-light text-secondary border fw-normal mt-1" style={{ fontSize: '10px' }}>

                {item.variationName}

              </span>

            )} */}
              </td>

              <td className="d-none d-md-table-cell px-3 py-1 text-secondary small font-monospace">
                {item.itemId}
              </td>

              <td className="px-2 px-md-3 py-1 text-end text-secondary fw-medium small">
                {item.quantity}
              </td>

              <td className="px-2 px-md-3 py-1 text-end fw-semibold text-dark small">
                {item.amount.toLocaleString(CONFIG.LOCALE)}
              </td>
            </tr>
          ))}

        {/* Subtotal Row */}

        {isExpanded && (
          <tr
            className="table-primary"
            style={{
              "--bs-table-bg": "rgba(13, 110, 253, 0.05)",
              "--bs-table-border-color": "rgba(13, 110, 253, 0.1)",
            }}
          >
            <td className="px-2 px-md-3 py-2"></td>

            <td className="px-2 px-md-3 py-2 text-end text-primary fw-semibold small">
              {category.categoryName} Total
            </td>

            {/* Hidden filler for Code column */}

            <td className="d-none d-md-table-cell border-bottom-0"></td>

            <td className="px-2 px-md-3 py-2 text-end text-primary fw-semibold small">
              {category.subTotalQuantity}
            </td>

            <td className="px-2 px-md-3 py-2 text-end text-primary fw-semibold small">
              {formatCurrency(category.subTotalAmount)}
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  }
);

const ZamzamSalesReport = () => {
  // --- STATE ---

  const [data, setData] = useState(null);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(null);
  const {
    sidebar,
    setSidebar,
    Dark,
    setDark,
    selectedStoreID,
    selectedRestID,
    ApiConfig,
    storesList,
    permissions,
    setSelectedStoreID, 
  } = useContext(AppContext);

  const [expandedCategories, setExpandedCategories] = useState({});

  const getTodayDate = () => new Date().toISOString().split('T')[0];

  const [filters, setFilters] = useState({
    dateFrom: getTodayDate(),

    dateTo: getTodayDate(),
    status: "",

    category: "",
    item: "",
  });

  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const [loadingExport, setLoadingExport] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500); // 👈 delay (ms)

    return () => clearTimeout(timer);
  }, [filters]);

  const [kitchens, setKitchens] = useState([]); // To store kitchen list

  // Fetch kitchens when store changes
  const fetchKitchens = async () => {
    try {
      const res = await simpleGetCall(`${ApiConfig.List_Print}${selectedStoreID}`);
      if (res.success) {
        setKitchens(res.data);
      }
    } catch (error) {
      console.error("Error fetching kitchens:", error);
    }
  };

  useEffect(() => {
    if (selectedStoreID) {
      fetchKitchens();
    }
  }, [selectedStoreID]);
  const DonloadReport = async (kitchenId) => {

    try {
      const restaurantName = filterRestaurantName(selectedStoreID, storesList);
      const url = `${ApiConfig.ReportExdownlod}?start_date=${debouncedFilters.dateFrom}&end_date=${debouncedFilters.dateTo}&order_status=&category=&item=&shop_id=${selectedStoreID}&kitchen_id=${kitchenId}&restaurant_name=${restaurantName}`;

      const res = await simpleGetCall(url);

      if (res.success) {
        notifySuccess("Report generated successfully!");
        if (res.data?.url) window.open(res.data.url); // Adjust based on your API response
      } else {
        notifyError("Download failed: " + res.message);
      }
    } catch (err) {
      console.error("Download Error:", err);
      notifyError("Something went wrong");
    } finally {

    }
  };

  const getSalesReport = async () => {
    setLoading(true);

    try {
      const res = await simpleGetCall(
        ` ${ApiConfig?.GET_SALES_REPORT}?shop_id=${selectedStoreID}&start_date=${debouncedFilters?.dateFrom}&end_date=${debouncedFilters?.dateTo}&order_status=${debouncedFilters?.status}&category=${debouncedFilters?.category}&item=${debouncedFilters?.item}`
      );

      const itemlist = res.data;
      if (res.success) {
        setData(itemlist);


        if (res?.data?.categoryWiseData) {
          const initialExpandedState = res?.data?.categoryWiseData.reduce(
            (acc, cat) => ({
              ...acc,

              [cat.categoryId]: true,
            }),
            {}
          );

          setExpandedCategories(initialExpandedState);
        }
      } else {
      }
    } catch (err) {
      console.error("Status API Error:", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (selectedStoreID) {
      getSalesReport();
    }
  }, [selectedStoreID, debouncedFilters]);

  function downloadExcelFromResponse(response, fileName = "report.xlsx") {
    setLoadingExport(true);
    response.blob().then((blob) => {
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);
    });

    setLoadingExport(false);
  }
  const getSalesReportExport = async () => {
    setLoadingExport(true);
    if (!selectedStoreID) {
      notifyError("Please select a restaurant");
      setLoadingExport(false);
      return;
    }

    try {
      const response = await fetch(
        ` ${ApiConfig?.GET_ITEM_WISE_SALES_REPORT_EXPORTS}?shop_id=${selectedStoreID}&start_date=${debouncedFilters?.dateFrom}&end_date=${debouncedFilters?.dateTo}&order_status=${debouncedFilters?.status}&category=${debouncedFilters?.category}&item=${debouncedFilters?.item}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("Token"),
          },
        }
      );

      if (!response.ok) {
        throw new Error("Download failed");
      }

      downloadExcelFromResponse(response, "reports.xlsx");
    } catch (err) {
      notifyError("Something went wrong");
      console.error("Status API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // --- EFFECTS ---

  // 1. Inject Bootstrap CSS

  // NOTE: In a real production build, this should be removed and the CSS should be imported

  // via 'import "bootstrap/dist/css/bootstrap.min.css"' or included in index.html.

  // useEffect(() => {

  //   const link = document.createElement('link');

  //   link.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css";

  //   link.rel = "stylesheet";

  //   document.head.appendChild(link);

  //   return () => {

  //     document.head.removeChild(link);

  //   };

  // }, []);

  // 2. Data Fetching

  // const loadData = useCallback(async () => {

  //   try {

  //     setLoading(true);

  //     setError(null);

  //     const response = await fetchReportData();

  //     setData(response);

  //     // Initialize all categories as expanded by default

  //     if (response && response.categoryWiseData) {

  //       const initialExpandedState = response.categoryWiseData.reduce((acc, cat) => ({

  //         ...acc,

  //         [cat.categoryId]: true

  //       }), {});

  //       setExpandedCategories(initialExpandedState);

  //     }

  //     setLoading(false);

  //   } catch (err) {

  //     console.error("Failed to load data", err);

  //     setError("Failed to load sales report data. Please try again.");

  //     setLoading(false);

  //   }

  // }, []);

  // useEffect(() => {

  //   loadData();

  // }, [loadData]);

  // --- HANDLERS ---

  const toggleCategory = useCallback((id) => {
    setExpandedCategories((prev) => ({
      ...prev,

      [id]: !prev[id],
    }));
  }, []);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;

    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  // --- DERIVED STATE ---

  const categories = useMemo(() => {
    if (!data) return ["All"];

    return ["All", ...data.categoryWiseData.map((c) => c.categoryName)];
  }, [data]);

  const filteredData = useMemo(() => {
    if (!data) return [];

    return data.categoryWiseData.filter((cat) => {
      // if (filters.category !== 'All' && cat.categoryName !== filters.category) return false;

      return true;
    });
  }, [filters, data]);

  // --- RENDER ---

  if (loading) {
    return (
      <div
        className={sidebar ? "taskMain " : "cx-active taskMain"}
        id="cx-main"
      >
        <div className="role-content">
          <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light">
            <div className="text-center">
              <div className="spinner-border text-primary mb-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>

              <p className="text-secondary small">Loading Sales Report...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={sidebar ? "taskMain " : "cx-active taskMain"}
        id="cx-main"
      >
        <div className="role-content">
          <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light">
            <div
              className="alert alert-danger d-flex flex-column align-items-center gap-3 shadow-sm border-0"
              role="alert"
            >
              <div className="d-flex align-items-center gap-2">
                <AlertCircle size={20} />

                <div>{error}</div>
              </div>

              <button
                onClick={getSalesReport}
                className="btn btn-sm btn-outline-danger d-flex align-items-center gap-2"
              >
                <RefreshCw size={14} /> Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className={sidebar ? "taskMain " : "cx-active taskMain"} id="cx-main">
      <div className="role-content-main">
        <div className="bg-light min-vh-100 p-2 p-md-3">
          <div
            className="container-fluid px-0 px-md-3" /* style={{ maxWidth: '1480px' }} */
          >
            {/* Header Section */}

            <div className="card shadow-sm border-0 mb-3">
              <div className="card-body p-3">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                  <div>
                    <h1 className="h5 fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                      <Receipt className="text-primary" size={20} />
                      Item Wise Sales Report
                    </h1>

                    <div className="text-secondary small">
                      <div className="d-flex align-items-center gap-2">
                        <Store size={14} />
                        <span className="fw-medium">Restaurant:</span>{" "}
                        {filterRestaurantName(selectedStoreID, storesList)}
                      </div>
                    </div>
                  </div>
                    <div className="">
                     <span>Select Restaurants:</span>
                    {permissions && permissions["View Item Wise Sales Report"] && (
              <StoreSelector
                options={Array.isArray(storesList) ? storesList.map(store => ({
                  value: store.value,
                  label: store.label,
                })) : []}
                value={selectedStoreID}
                onChange={(value) => {
                  setSelectedStoreID(value);
                  localStorage.setItem("selectedStoreId", value);
                }}
              />
            )}
            </div>
                  {
                    permissions && permissions['Export Item Wise Sales Report'] &&

                    <div className="mt-2 mt-md-0">
                     

                      <div className="mt-2 mt-md-0 d-flex gap-2">
                        {/* Your Existing Button */}
                        <button
                          className="btn btn-primary btn-sm d-flex align-items-center gap-2 shadow-sm w-100 w-md-auto justify-content-center"
                          onClick={getSalesReportExport}
                          disabled={loadingExport}
                        >
                          <Download size={14} />
                          {loadingExport ? <Spinner size="sm" /> : "Download Excel"}
                        </button>

                        {/* New Kitchen-wise Dropdown Button */}


                        <div className="dropdown">
                          <button
                            className="btn btn-outline-primary btn-sm dropdown-toggle d-flex align-items-center gap-2 shadow-sm"
                            type="button"
                            data-bs-toggle="dropdown"
                            disabled={loadingExport || kitchens.length === 0}
                          >
                            <Download size={14} />
                            Thermal Print
                          </button>
                          <ul className="dropdown-menu dropdown-menu-end shadow">
                            <li><h6 className="dropdown-header">Thermal Print</h6></li>
                            {kitchens.map((k) => (
                              <li key={k.id}>
                                <button
                                  className="dropdown-item small"
                                  onClick={() => DonloadReport(k.id)}
                                >
                                  {k.kitchen_name}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>

            {/* Summary Cards */}

            <div className="row g-2 mb-3">
              <div className="col-6 col-md-3">
                <SummaryCard
                  title="Total Sales"
                  value={formatCurrency(data?.summary?.totalAmount)}
                  subValue={`${data?.summary?.totalQuantity} items sold`}
                  icon={<IndianRupee size={16} className="text-success" />}
                  colorClass="bg-success-subtle border-success-subtle text-success"
                />
              </div>

              <div className="col-6 col-md-3">
                <SummaryCard
                  title="Avg Amount"
                  value={formatCurrency(data?.summary?.avgAmount)}
                  subValue="Per item"
                  icon={<BarChart3 size={16} className="text-primary" />}
                  colorClass="bg-primary-subtle border-primary-subtle text-primary"
                />
              </div>

              <div className="col-6 col-md-3">
                <SummaryCard
                  title="Highest Sale"
                  value={formatCurrency(data?.summary?.maxAmount)}
                  subValue={`${data?.summary?.maxQuantity} qty in one order`}
                  icon={<ChevronDown size={16} className="text-info" />}
                  colorClass="bg-info-subtle border-info-subtle text-info"
                  rotateIcon={true}
                />
              </div>

              <div className="col-6 col-md-3">
                <SummaryCard
                  title="Lowest Sale"
                  value={formatCurrency(data?.summary?.minAmount)}
                  subValue={`${data?.summary?.minQuantity} qty in one order`}
                  icon={<ChevronDown size={16} className="text-warning" />}
                  colorClass="bg-warning-subtle border-warning-subtle text-warning"
                />
              </div>
            </div>

            {/* Main Data Table */}

            <div className="card shadow-sm border-0">
              {/* Filters Bar */}

              <div className="card-header bg-light py-2 border-bottom">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                  <h2 className="h6 mb-0 fw-semibold text-dark">
                    Detailed Breakdown
                  </h2>

                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {/* Date From */}

                    <div
                      className="input-group input-group-sm flex-nowrap"
                      style={{ width: "auto", flex: "1 0 auto" }}
                    >
                      <span
                        className="input-group-text--- bg-white--- text-secondary small fw-semibold"
                        style={{ fontSize: "10px" }}
                      >
                        FROM:
                      </span>

                      <input
                        type="date"
                        name="dateFrom"
                        value={filters.dateFrom}
                        onChange={handleFilterChange}
                        className="form-control form-control-sm border-start-0 text-secondary"
                        style={{
                          fontSize: "12px",
                          minWidth: "95px",
                          maxWidth: "110px",
                        }}
                      />
                    </div>

                    {/* Date To */}

                    <div
                      className="input-group input-group-sm flex-nowrap"
                      style={{ width: "auto", flex: "1 0 auto" }}
                    >
                      <span
                        className="input-group-text--- bg-white--- text-secondary small fw-semibold"
                        style={{ fontSize: "10px" }}
                      >
                        TO:
                      </span>

                      <input
                        type="date"
                        name="dateTo"
                        value={filters.dateTo}
                        onChange={handleFilterChange}
                        className="form-control form-control-sm border-start-0 text-secondary"
                        style={{
                          fontSize: "12px",
                          minWidth: "95px",
                          maxWidth: "110px",
                        }}
                      />
                    </div>

                    {/* Status Filter */}

                    <select
                      name="status"
                      value={filters.status}
                      onChange={handleFilterChange}
                      className="form-select form-select-sm text-secondary flex-grow-1 flex-md-grow-0"
                      style={{
                        width: "auto",
                        fontSize: "12px",
                        minWidth: "100px",
                      }}
                    >
                      <option value="all">Status: All</option>
                      <option value="pending">Pending</option>
                      <option value="accepted">Accepted</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="delivered">Delivered</option>
                      <option value="ready_to_deliver">Ready To Deliver</option>
                    </select>

                    {/* Category Input */}
                    <input
                      type="text"
                      name="category"
                      value={filters.category}
                      onChange={handleFilterChange}
                      placeholder="Category"
                      className="form-control form-control-sm text-secondary"
                      style={{
                        width: "auto",
                        fontSize: "12px",
                        minWidth: "120px",
                      }}
                    />

                    {/* Item Input */}
                    <input
                      type="text"
                      name="item"
                      value={filters.item}
                      onChange={handleFilterChange}
                      placeholder="Item"
                      className="form-control form-control-sm text-secondary"
                      style={{
                        width: "auto",
                        fontSize: "12px",
                        minWidth: "120px",
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-hover mb-0 align-middle">
                  <thead className="table-light text-uppercase small text-secondary">
                    <tr>
                      <th
                        className="px-2 px-md-3 py-2 fw-semibold"
                        style={{ width: "25%" }}
                      >
                        Category
                      </th>

                      <th
                        className="px-2 px-md-3 py-2 fw-semibold"
                        style={{ width: "40%" }}
                      >
                        Item Name
                      </th>

                      <th
                        className="d-none d-md-table-cell px-3 py-2 fw-semibold"
                        style={{ width: "10%" }}
                      >
                        Code
                      </th>

                      <th
                        className="px-2 px-md-3 py-2 fw-semibold text-end"
                        style={{ width: "10%" }}
                      >
                        Qty
                      </th>

                      <th
                        className="px-2 px-md-3 py-2 fw-semibold text-end"
                        style={{ width: "15%" }}
                      >
                        Amount (₹)
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredData.length > 0 ? (
                      filteredData.map((category, idx) => (
                        <CategoryRow
                          key={category.categoryId}
                          category={category}
                          idx={idx}
                          isExpanded={!!expandedCategories[category.categoryId]}
                          toggleCategory={toggleCategory}
                        />
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="5"
                          className="text-center py-4 text-secondary"
                        >
                          No data found matching your filters.
                        </td>
                      </tr>
                    )}
                  </tbody>

                  <tfoot className="table-dark">
                    <tr>
                      <td className="px-2 px-md-3 py-3"></td>

                      <td className="px-2 px-md-3 py-3 text-end fw-bold text-uppercase small">
                        Grand Total
                      </td>

                      {/* Hidden filler for Code column */}

                      <td className="d-none d-md-table-cell border-bottom-0"></td>

                      <td className="px-2 px-md-3 py-3 text-end fw-bold fs-6">
                        {data?.summary?.totalQuantity}
                      </td>

                      <td className="px-2 px-md-3 py-3 text-end fw-bold fs-6 text-success">
                        {formatCurrency(data?.summary?.totalAmount)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div >
  );
};

export default ZamzamSalesReport;
